import styled from 'styled-components'

import {
  ApproveIcon,
  RejectIcon,
  SmallApprovedIcon,
  TileRejectedIcon as SmallRejectedIcon,
} from 'Icons'

import { Title, Desc, FlexAlignCenter } from './CommonStyleComponents'

export interface PostActionProps {
  postId: string
  isLoading: boolean
  postStatus?: string
  isMobile?: boolean
  localPostStatus?: string
  reviewKey: string
  className?: string
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
}

const PostAction = (props: PostActionProps) => {
  const {
    postId,
    isLoading,
    postStatus,
    isMobile,
    localPostStatus,
    reviewKey,
    updatePostState,
    className,
  } = props
  if (!postStatus) return null

  const handleUndo = () => {
    updatePostState(reviewKey, postId, 'pending')
  }

  const handleApproved = () => {
    updatePostState(reviewKey, postId, 'approved')
  }

  const handleRequestChange = () => {
    updatePostState(reviewKey, postId, 'requestChange')
  }

  const isApproved = postStatus === 'approved'
  const isRequestChange = postStatus === 'requestChange'

  return (
    <div className={className}>
      {!isMobile && (
        <>
          <Title>Post Actions</Title>
          <Desc>Approve or request changes for the post.</Desc>
        </>
      )}

      <ActionButton $isLoading={isLoading}>
        {(isApproved || isRequestChange) && (
          <FlexAlignCenterStyled $isApproved={isApproved} $isMobile={isMobile}>
            <div>
              {isApproved ? 'Approved!' : 'Change requested!'}
              <Undo onClick={handleUndo}>Undo</Undo>
            </div>
            {isApproved ? <ApproveIcon /> : <RejectIcon />}
          </FlexAlignCenterStyled>
        )}
      </ActionButton>

      {isMobile && localPostStatus !== 'pending' && (
        <StatusChangedWrap>
          <hr />

          {postStatus === 'approved' && (
            <div>
              <div>
                <SmallApprovedIcon />
                Post approved
              </div>
              <p>To undo this action, visit this link on a desktop device</p>
            </div>
          )}
          {postStatus === 'requestChange' && (
            <div>
              <div>
                <SmallRejectedIcon />
                <div>Change requested</div>
              </div>
              <p>To undo this action, visit this link on a desktop device</p>
            </div>
          )}
        </StatusChangedWrap>
      )}

      <ActionButtons
        $isMobile={isMobile}
        $isLoading={isLoading}
        $hasMarginBottom={localPostStatus !== 'pending' && !isLoading}
      >
        {postStatus === 'pending' && (
          <div>
            <div
              data-testid="approve"
              className="approve"
              onClick={handleApproved}
            >
              <div>approve</div>
            </div>

            <div
              data-testid="requestChange"
              className="requestChange"
              onClick={handleRequestChange}
            >
              <div>
                <p>request change</p>
              </div>
            </div>
          </div>
        )}
      </ActionButtons>
    </div>
  )
}

const ActionButtons = styled.div<{
  $isMobile?: boolean
  $isLoading: boolean
  $hasMarginBottom?: boolean
}>`
  ${({ $isMobile, $hasMarginBottom }) =>
    $isMobile &&
    !$hasMarginBottom &&
    `margin-bottom: 12px;
`}

  > div {
    height: 78px;
    width: 100%;
    display: flex;
    gap: ${(props) => (props.$isMobile ? '24px' : '38px')};
    cursor: pointer;
    justify-content: ${(props) => (props.$isMobile ? 'center' : 'initial')};

    ${(props) =>
      props.$isLoading &&
      `
      cursor: wait;
      opacity: 0.7;

      > .approve, .requestChange {

        &:hover {
          box-shadow: none;
        }
      }
    `}

    > .approve,
    .requestChange {
      border-radius: 5px;
      font-size: 18px;
      color: #ffffff;
      text-transform: uppercase;
      height: 100%;
      text-align: center;
      display: grid;
      place-items: center;
      min-width: 160px;
      font-weight: 700;
      line-height: 115%;
    }

    > .approve {
      background: #66b913;

      &:hover {
        box-shadow: 0 4px 10px rgba(102, 185, 19, 0.4);
      }
    }

    > .requestChange {
      background: #fa4f38;

      p {
        margin: 0 auto;
        width: 100px;
        white-space: break-spaces;
      }

      &:hover {
        box-shadow: 0 4px 10px rgba(250, 79, 56, 0.4);
      }
    }
  }
`

const ActionButton = styled.div<{ $isLoading: boolean }>`
  ${(props) =>
    props.$isLoading &&
    `
  cursor: wait;
  opacity: 0.7;
  
  div {
    cursor: wait;
  }
`}
`

const FlexAlignCenterStyled = styled(FlexAlignCenter)<{
  $isApproved?: boolean
  $isMobile?: boolean
}>`
  width: ${(props) => (props.$isMobile ? '100%' : '360px')};
  padding: 12px;
  border-radius: 5px;
  white-space: nowrap;
  background: ${(props) => (props.$isApproved ? '#f0f8e7' : '#fee5e1')};
  position: relative;

  > div {
    position: absolute;
    left: ${(props) =>
      props.$isApproved
        ? 'calc(50% - 78px)'
        : 'calc(40% - 78px)'}; //different text length
    text-align: center;
    font-size: 16px;
  }

  > svg {
    background: ${(props) => (props.$isApproved ? '#66B914' : '#FA4F38')};
    border-radius: 5px;
    min-width: 78px;
    height: 78px;
    margin: -12px;
    margin-left: auto;
  }
`

const Undo = styled.div`
  text-decoration: underline;
  font-size: 12px;
  color: #4f31ff;
  cursor: pointer;
  font-style: italic;
`

const StatusChangedWrap = styled.div`
  margin-bottom: 10px;

  > div > div {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 28px;
      margin-right: 10px;
    }
  }

  p {
    text-align: center;
    font-size: 12px;
    margin: 10px 0 0;
  }

  hr {
    border: none;
    border-bottom: 1px solid #dedafe;
    margin: 12px auto 16px;
  }
`

export default PostAction
